import { useEffect, useRef, useCallback } from "react";
import { Capacitor } from "@capacitor/core";
import { PushNotifications, Token } from "@capacitor/push-notifications";
import { useAuth } from "@/contexts/AuthContext";
import { 
  initializePushNotifications, 
  unregisterPushNotifications,
  showLocalNotification
} from "@/lib/pushNotifications";
import { 
  initializeWebPush, 
  isWebPushSupported,
  requestNotificationPermission,
  isNotificationPermissionGranted 
} from "@/lib/webPush";
import { 
  startBackgroundPolling, 
  stopBackgroundPolling,
  forceNotificationCheck 
} from "@/lib/backgroundNotifications";
import { supabase } from "@/integrations/supabase/client";

/**
 * Unified Push Notification Manager
 * 
 * This component handles push notifications for all platforms:
 * - Native Android: Uses Capacitor PushNotifications
 * - Native iOS: Uses Capacitor PushNotifications  
 * - WebApp: Uses Web Push API with Service Worker
 * 
 * Automatically detects platform and initializes the correct method.
 */
export function PushNotificationManager() {
  const { user } = useAuth();
  const initialized = useRef(false);

  // Handle notification click
  const handleNotificationClick = useCallback((data: any) => {
    console.log("[PushManager] Notification clicked:", data);
    
    // Navigate based on notification type
    if (data?.type && data?.bookingId) {
      window.location.href = `/reservas/${data.bookingId}`;
    } else if (data?.type === "new_message" && data?.conversationId) {
      window.location.href = `/chat/${data.conversationId}`;
    } else if (data?.type?.includes("affiliate")) {
      window.location.href = "/afiliado/painel";
    } else if (data?.type?.includes("withdrawal") || data?.type === "host_balance_converted") {
      window.location.href = "/dashboard/financas";
    } else {
      window.location.href = "/notificacoes";
    }
  }, []);

  // Initialize native push (Android/iOS)
  const initNativePush = useCallback(async (userId: string) => {
    if (!Capacitor.isNativePlatform()) {
      return false;
    }

    try {
      console.log("[PushManager] Initializing native push notifications...");
      
      // Use the existing native push system
      await initializePushNotifications(userId);
      
      // Listen for push notification clicks
      PushNotifications.addListener("pushNotificationActionPerformed", (action) => {
        console.log("[PushManager] Push action performed:", action);
        handleNotificationClick(action.notification.data);
      });

      // Listen for foreground notifications
      PushNotifications.addListener("pushNotificationReceived", (notification) => {
        console.log("[PushManager] Push received in foreground:", notification);
        
        // Dispatch custom event for UI updates
        window.dispatchEvent(new CustomEvent("push-notification-received", {
          detail: notification
        }));
      });

      console.log("[PushManager] Native push initialized");
      return true;
    } catch (error) {
      console.error("[PushManager] Native push init error:", error);
      return false;
    }
  }, [handleNotificationClick]);

  // Initialize web push (WebApp)
  const initWebPush = useCallback(async (userId: string) => {
    if (Capacitor.isNativePlatform()) {
      return false;
    }

    if (!isWebPushSupported()) {
      console.log("[PushManager] Web push not supported");
      return false;
    }

    try {
      console.log("[PushManager] Initializing web push notifications...");

      // Check permission
      if (!isNotificationPermissionGranted()) {
        console.log("[PushManager] Notification permission not granted");
        return false;
      }

      // Initialize web push
      const success = await initializeWebPush(userId);
      
      if (success) {
        console.log("[PushManager] Web push initialized");
        
        // Listen for service worker messages (notification clicks from SW)
        if ("serviceWorker" in navigator) {
          navigator.serviceWorker.addEventListener("message", (event) => {
            if (event.data?.type === "notification-click") {
              console.log("[PushManager] Notification click from SW:", event.data);
              handleNotificationClick(event.data.data);
            }
          });
        }
      }
      
      return success;
    } catch (error) {
      console.error("[PushManager] Web push init error:", error);
      return false;
    }
  }, [handleNotificationClick]);

  // Main initialization effect
  useEffect(() => {
    if (!user?.id || initialized.current) {
      return;
    }

    const init = async () => {
      initialized.current = true;

      const isNative = Capacitor.isNativePlatform();
      console.log(`[PushManager] Initializing for user ${user.id}, platform: ${isNative ? 'native' : 'web'}`);

      if (isNative) {
        await initNativePush(user.id);
        // 🆕 Inicia background polling como fallback (quando FCM não disponível)
        await startBackgroundPolling(user.id, 5); // Verifica a cada 5 minutos
      } else {
        await initWebPush(user.id);
      }

      // Subscribe to realtime notifications via Supabase
      const channel = supabase
        .channel(`notifications:${user.id}`)
        .on(
          "postgres_changes",
          {
            event: "INSERT",
            schema: "public",
            table: "push_notifications",
            filter: `user_id=eq.${user.id}`,
          },
          (payload) => {
            console.log("[PushManager] Realtime notification received:", payload);
            const notification = payload.new as any;
            
            // Show local notification on all platforms
            if (isNative) {
              // Android/iOS: Show local notification triggered by Realtime
              showLocalNotification(
                notification.title || "BeraStays",
                notification.message,
                {
                  ...notification.data,
                  notificationId: notification.id,
                  type: notification.type,
                }
              );
            } else if (isWebPushSupported() && isNotificationPermissionGranted()) {
              // Web: show notification via Service Worker
              if ("serviceWorker" in navigator) {
                navigator.serviceWorker.ready.then((registration) => {
                  registration.showNotification(notification.title || "BeraStays", {
                    body: notification.message,
                    icon: "/apple-touch-icon.png",
                    badge: "/favicon-32x32.png",
                    data: {
                      ...notification.data,
                      notificationId: notification.id,
                      type: notification.type,
                    },
                    tag: notification.type || "default",
                    requireInteraction: true,
                  });
                });
              }
            }
            
            // Dispatch event for notification badge/count updates
            window.dispatchEvent(new CustomEvent("new-notification", {
              detail: notification,
            }));
          }
        )
        .subscribe();

      // Cleanup
      return () => {
        supabase.removeChannel(channel);
      };
    };

    init();
  }, [user?.id, initNativePush, initWebPush]);

  // Handle logout - unregister push
  useEffect(() => {
    if (!user && initialized.current) {
      // User logged out, unregister
      const logoutUserId = localStorage.getItem("last_user_id");
      if (logoutUserId) {
        if (Capacitor.isNativePlatform()) {
          unregisterPushNotifications(logoutUserId);
          // 🆕 Para background polling
          stopBackgroundPolling();
        } else {
          import("@/lib/webPush").then(({ unsubscribeFromPushNotifications }) => {
            unsubscribeFromPushNotifications(logoutUserId);
          });
        }
      }
    } else if (user?.id) {
      // Store user ID for logout handling
      localStorage.setItem("last_user_id", user.id);
    }
  }, [user]);

  // This is an invisible component
  return null;
}

/**
 * Hook to request notification permission
 * Should be called during onboarding
 */
export async function requestPushPermission(): Promise<boolean> {
  const isNative = Capacitor.isNativePlatform();

  if (isNative) {
    try {
      const result = await PushNotifications.requestPermissions();
      return result.receive === "granted";
    } catch {
      return false;
    }
  } else {
    return requestNotificationPermission();
  }
}

/**
 * Check if push notifications are enabled
 */
export async function arePushNotificationsEnabled(): Promise<boolean> {
  const isNative = Capacitor.isNativePlatform();

  if (isNative) {
    try {
      const status = await PushNotifications.checkPermissions();
      return status.receive === "granted";
    } catch {
      return false;
    }
  } else {
    return isNotificationPermissionGranted();
  }
}
